/* The dataset's profiling runs on one line, oldest at the left, with the profile baseline drawn
 * through them and the two compared runs banded.
 *
 * **A run is chosen by pointing at it.** Selecting a run makes it the later side and compares it with
 * the run before it, which is the question a reader most often has ("what changed in this run?"); the
 * two pickers above the strip are for the less common pair that is not adjacent.
 *
 * **A run with no baseline is drawn hollow on the floor, never at zero**, and it breaks the line rather
 * than being joined to its neighbours: a line dipping to the floor would read as the baseline
 * collapsing, which is a claim about the data where the truth is that nothing was measured. A run
 * stored before run markers existed is hollow too, and the footer says why, because its grouping by
 * schema version is a fact about how it was stored rather than about the data.
 */

import type { TFunction } from 'i18next';
import type { CSSProperties, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProfileRunSummary } from '@/shared/api/generated/catalog';

import { HISTORY_RUNS, MAX_HISTORY_RUNS } from '../api/profiler';
import { STRIP, dayLabel, instantLabel, stripGeometry } from '../model/history';
import type { Pair } from '../model/history';
import { runFacts, runOption } from './historyCopy';

/** The pickers' value for "let the platform choose". */
export const LATEST = 'latest';
export const AUTO = 'auto';

export interface RunStripProps {
  readonly runs: readonly ProfileRunSummary[];
  /** The chosen pair, or `null` while there is only one run. */
  readonly pair: Pair | null;
  readonly laterValue: string;
  readonly earlierValue: string;
  readonly onLaterChange: (value: string) => void;
  readonly onEarlierChange: (value: string) => void;
  /** Make the run at this index the later side, compared with the run before it. */
  readonly onPick: (index: number) => void;
  readonly truncated: boolean;
  readonly limit: number;
  readonly onLoadMore: () => void;
  readonly comparing: boolean;
}

type Tag = 'later' | 'earlier';

export function RunStrip({
  runs,
  pair,
  laterValue,
  earlierValue,
  onLaterChange,
  onEarlierChange,
  onPick,
  truncated,
  limit,
  onLoadMore,
  comparing,
}: RunStripProps): ReactElement {
  const { t } = useTranslation();
  const geometry = stripGeometry(runs);
  const tagOf = (index: number): Tag | null => {
    if (pair === null) return null;
    if (index === pair.later) return 'later';
    if (pair.block === null && index === pair.earlier) return 'earlier';
    return null;
  };
  const xOf = (index: number): number | undefined =>
    geometry.nodes.find((node) => node.index === index)?.x;
  /* The band spans the compared pair only while that pair can be compared. */
  const banded = pair?.block === null ? pair : null;
  const bandLeft = banded === null ? undefined : xOf(banded.earlier);
  const bandRight = banded === null ? undefined : xOf(banded.later);
  const untracked = runs.some((run) => !run.tracked);
  /* "All" only after asking for more: a short history that was never truncated needs no sentence. */
  const loadedAll = !truncated && limit > HISTORY_RUNS;
  const next = pair === null ? undefined : runs[pair.later + 1];

  return (
    <article className="lq-ph__strip">
      <div className="lq-ph__stripHead">
        <div className="lq-ph__stripTitles">
          <div className="lq-ph__titleRow">
            <h2 className="lq-ph__title">{t('profile.history.runs.title')}</h2>
            <span className="lq-pp__count">
              {truncated
                ? t('profile.history.runs.countMore', { count: runs.length })
                : runs.length}
            </span>
          </div>
          <p className="lq-ph__sub">{t('profile.history.runs.sub')}</p>
        </div>
        {pair !== null && (
          <div className="lq-ph__pickers">
            <label className="lq-ph__picker">
              {t('profile.history.pick.earlier')}
              <select
                className="lq-ph__select"
                aria-label={t('profile.history.pick.earlierLabel')}
                value={earlierValue}
                onChange={(event) => {
                  onEarlierChange(event.target.value);
                }}
              >
                <option value={AUTO}>
                  {next === undefined
                    ? t('profile.history.pick.beforeNone')
                    : t('profile.history.pick.before', {
                        day: dayLabel(next.finished_at),
                      })}
                </option>
                {runs.map((run, index) => (
                  <option
                    key={run.run_id}
                    value={run.run_id}
                    disabled={index < pair.later}
                  >
                    {runOption(t, run)}
                  </option>
                ))}
              </select>
            </label>
            <span className="lq-ph__arrow" aria-hidden="true">
              →
            </span>
            <label className="lq-ph__picker">
              {t('profile.history.pick.later')}
              <select
                className="lq-ph__select"
                aria-label={t('profile.history.pick.laterLabel')}
                value={laterValue}
                onChange={(event) => {
                  onLaterChange(event.target.value);
                }}
              >
                <option value={LATEST}>
                  {t('profile.history.pick.latest', {
                    day: dayLabel(runs[0]?.finished_at ?? ''),
                  })}
                </option>
                {runs.map((run) => (
                  <option key={run.run_id} value={run.run_id}>
                    {runOption(t, run)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>

      <div className="lq-ph__stripScroll">
        <div
          className="lq-ph__plot"
          role="group"
          aria-label={t('profile.history.strip.label')}
          style={{ '--ph-strip-min': `${String(geometry.minWidth)}px` } as CSSProperties}
        >
          {bandLeft !== undefined && bandRight !== undefined && (
            <div
              className="lq-ph__band"
              style={
                {
                  '--ph-band-left': `${bandLeft.toFixed(2)}%`,
                  '--ph-band-width': `${(bandRight - bandLeft).toFixed(2)}%`,
                } as CSSProperties
              }
            />
          )}
          <div className="lq-ph__gridLine lq-ph__gridLine--top" />
          <div className="lq-ph__gridLine lq-ph__gridLine--bottom" />
          <svg
            className="lq-ph__svg"
            viewBox={`0 0 100 ${String(STRIP.plot)}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          >
            {geometry.lines.map((line) => (
              <g key={line.points}>
                <polygon className="lq-ph__area" points={line.area} />
                <polyline
                  className="lq-ph__line"
                  points={line.points}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            ))}
          </svg>
          <span className="lq-ph__scale lq-ph__scale--high" aria-hidden="true">
            {geometry.high}
          </span>
          <span className="lq-ph__scale lq-ph__scale--low" aria-hidden="true">
            {geometry.low}
          </span>
          {geometry.nodes.map((node) => {
            const tag = tagOf(node.index);
            const baseline =
              node.run.baseline_value === null
                ? '—'
                : String(Math.round(node.run.baseline_value));
            const dot = [
              'lq-ph__dot',
              tag === 'later'
                ? 'lq-ph__dot--later'
                : tag === 'earlier'
                  ? 'lq-ph__dot--earlier'
                  : !node.run.tracked || node.y === null
                    ? 'lq-ph__dot--hollow'
                    : '',
            ]
              .filter((part) => part !== '')
              .join(' ');
            return (
              <button
                key={node.run.run_id}
                type="button"
                className="lq-ph__node"
                title={nodeTitle(t, node.run, node.index === runs.length - 1)}
                aria-label={t('profile.history.run.label', {
                  run: runOption(t, node.run),
                  baseline,
                })}
                aria-current={tag === null ? undefined : 'true'}
                onClick={() => {
                  if (pair !== null) onPick(node.index);
                }}
                style={
                  {
                    '--ph-x': `${node.x.toFixed(2)}%`,
                    '--ph-y': `${(node.y ?? STRIP.unmeasured).toFixed(1)}px`,
                  } as CSSProperties
                }
              >
                <span className={dot} />
                <span className="lq-ph__nodeLabel">
                  <span
                    className={tag === null ? 'lq-ph__day' : 'lq-ph__day lq-ph__day--on'}
                  >
                    {dayLabel(node.run.finished_at)}
                  </span>
                  {tag !== null && (
                    <span className={`lq-ph__tag lq-ph__tag--${tag}`}>
                      {tag === 'later'
                        ? t('profile.history.tag.later', { baseline })
                        : t('profile.history.tag.earlier', { baseline })}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="lq-ph__stripFoot">
        <span className="lq-ph__footItem">
          <span className="lq-ph__sourceDot" aria-hidden="true" />
          {t('profile.history.source')}
        </span>
        {untracked && (
          <span className="lq-ph__footItem">
            <span className="lq-ph__hollowKey" aria-hidden="true" />
            {t('profile.history.untracked')}
          </span>
        )}
        {truncated ? (
          <span className="lq-ph__footItem">
            {t('profile.history.truncated', { count: runs.length })}
            {limit < MAX_HISTORY_RUNS && (
              <button type="button" className="lq-ph__link" onClick={onLoadMore}>
                {t('profile.history.loadMore', { max: MAX_HISTORY_RUNS })}
              </button>
            )}
          </span>
        ) : loadedAll ? (
          <span className="lq-ph__footItem">
            {t('profile.history.all', { count: runs.length })}
          </span>
        ) : null}
      </div>

      {comparing && (
        <div
          className="lq-ph__progress"
          role="status"
          aria-label={t('profile.compare.loading')}
        >
          <div className="lq-ph__progressBar" />
        </div>
      )}
    </article>
  );
}

/** The run's tooltip: what it measured, and what selecting it does. The oldest listed run has nothing
 * before it, so it says that instead of promising a comparison. */
function nodeTitle(t: TFunction, run: ProfileRunSummary, oldest: boolean): string {
  const first = [
    instantLabel(run.finished_at),
    run.version_number === null
      ? null
      : t('profile.history.run.schema', { version: run.version_number }),
    runFacts(t, run),
    run.baseline_value === null
      ? t('profile.history.run.baselineNone')
      : t('profile.history.run.baseline', { value: Math.round(run.baseline_value) }),
    run.tracked ? null : t('profile.history.run.groupedLong'),
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
  const second = [
    t('profile.history.run.started', { when: instantLabel(run.started_at) }),
    oldest ? t('profile.history.run.oldest') : t('profile.history.run.pick'),
  ].join(' ');
  return `${first}\n${second}`;
}
